import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, CheckCircle2, XCircle, Search, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSimulator = () => {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  useEffect(() => {
    loadPendingInvoices();
  }, []);

  const loadPendingInvoices = async () => {
    setLoadingList(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/public/invoices/search?q=pending&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPendingInvoices(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setLoadingList(false);
    }
  };

  const searchInvoice = async () => {
    if (!invoiceNumber.trim()) {
      toast.error('Masukkan nomor invoice');
      return;
    }
    
    setLoading(true);
    setInvoiceData(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/public/invoices/search?q=${invoiceNumber}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.data?.length > 0) {
        setInvoiceData(response.data.data[0]);
      } else {
        toast.error('Invoice tidak ditemukan');
      }
    } catch (error) {
      toast.error('Gagal mencari invoice');
    } finally {
      setLoading(false);
    }
  };

  const simulatePayment = async (invoice) => {
    const inv = invoice || invoiceData;
    if (!inv) return;
    
    if (inv.status === 'paid') {
      toast.error('Invoice sudah dibayar');
      return;
    }
    
    setSimulating(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/public/payments/simulate`,
        {
          invoice_number: inv.invoice_number,
          amount: inv.total_amount
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Pembayaran berhasil disimulasikan!');
        // Refresh data
        if (invoiceData) {
          setInvoiceData({ ...inv, status: 'paid' });
        }
        loadPendingInvoices();
      } else {
        toast.error(response.data.message || 'Simulasi gagal');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Simulasi gagal');
    } finally {
      setSimulating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID').format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2 dark:text-slate-200">
          <Play className="w-6 h-6 text-green-600 dark:text-green-400" />
          Simulasi Pembayaran Duitku
        </h1>
        <p className="text-gray-600 mt-1 dark:text-slate-300">
          Tool untuk testing pembayaran di Sandbox mode
        </p>
      </div>

      {/* Search Box */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 dark:bg-slate-800">
        <h2 className="font-semibold mb-3">Cari Invoice</h2>
        <div className="flex gap-2">
          <Input
            placeholder="Masukkan nomor invoice (contoh: INV-202601-012066)"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && searchInvoice()}
            className="flex-1"
          />
          <Button onClick={searchInvoice} disabled={loading}>
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            <span className="ml-2">Cari</span>
          </Button>
        </div>

        {/* Search Result */}
        {invoiceData && (
          <div className="mt-4 p-4 border rounded-lg bg-gray-50 dark:bg-slate-900">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold text-lg">{invoiceData.invoice_number}</p>
                <p className="text-gray-600 dark:text-slate-300">{invoiceData.customer_name}</p>
                <p className="text-2xl font-bold text-green-600 mt-2 dark:text-green-400">
                  Rp {formatCurrency(invoiceData.total_amount)}
                </p>
              </div>
              <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  invoiceData.status === 'paid' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {invoiceData.status === 'paid' ? 'LUNAS' : 'PENDING'}
                </span>
                
                {invoiceData.status !== 'paid' && (
                  <Button 
                    onClick={() => simulatePayment(invoiceData)}
                    disabled={simulating}
                    className="mt-3 bg-green-600 hover:bg-green-700"
                  >
                    {simulating ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Simulasi Bayar
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pending Invoices List */}
      <div className="bg-white rounded-lg shadow p-4 dark:bg-slate-800">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold">Invoice Pending (Belum Bayar)</h2>
          <Button variant="outline" size="sm" onClick={loadPendingInvoices} disabled={loadingList}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loadingList ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loadingList ? (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
          </div>
        ) : pendingInvoices.length === 0 ? (
          <p className="text-gray-500 text-center py-8 dark:text-slate-400">Tidak ada invoice pending</p>
        ) : (
          <div className="space-y-2">
            {pendingInvoices.map((inv) => (
              <div 
                key={inv.id} 
                className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700/50"
              >
                <div>
                  <p className="font-medium">{inv.invoice_number}</p>
                  <p className="text-sm text-gray-600 dark:text-slate-300">{inv.customer_name}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    Rp {formatCurrency(inv.total_amount)}
                  </span>
                  <Button 
                    size="sm"
                    onClick={() => simulatePayment(inv)}
                    disabled={simulating}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Play className="w-3 h-3 mr-1" />
                    Simulasi
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/30">
        <h3 className="font-semibold text-blue-800 mb-2">ℹ️ Cara Kerja</h3>
        <ul className="text-sm text-blue-700 space-y-1 dark:text-blue-300">
          <li>• Klik <strong>"Simulasi Bayar"</strong> untuk mensimulasikan pembayaran sukses</li>
          <li>• Sistem akan trigger webhook callback seperti dari Duitku</li>
          <li>• Invoice akan otomatis berubah status menjadi <strong>LUNAS</strong></li>
          <li>• Customer yang ter-isolir akan otomatis dibuka aksesnya</li>
        </ul>
      </div>
    </div>
  );
};

export default PaymentSimulator;
